import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import WeatherBgUrl from "../../assets/MentalPlus/T_3.svg";
import { SequenceRenderCfg, WeatherKey } from "./IslandRegistry";

type Props = {
  data: { date: Date; score: number }[];
  renderCfg: SequenceRenderCfg;
  animateKey: number;
};

/**
 * Preload + decode an image URL so SVG <image> won't "pop in" after animation starts.
 */
function usePreloadedImage(url?: string) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!url) return;

    let cancelled = false;
    setReady(false);

    const img = new Image();
    img.src = url;

    const done = () => {
      if (!cancelled) setReady(true);
    };

    // decode() avoids the “loaded but not decoded/paintable yet” moment
    const decode = (img as any).decode as undefined | (() => Promise<void>);
    if (typeof decode === "function") {
      decode.call(img).then(done).catch(done);
    } else {
      img.onload = done;
      img.onerror = done;
    }

    return () => {
      cancelled = true;
    };
  }, [url]);

  return ready;
}

const EvolutionChart: React.FC<Props> = ({ data, renderCfg, animateKey }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const idRef = useRef<string>("");
  if (!idRef.current) idRef.current = `evo-${Math.random().toString(36).slice(2)}`;
  const uid = idRef.current;

  // ✅ Wait for background to be fetched + decoded before starting animation/drawing
  const bgReady = usePreloadedImage(WeatherBgUrl);

  useEffect(() => {
    if (!data.length || !svgRef.current) return;
    if (!bgReady) return;

    const width = 600;
    const height = 400;
    const margin = { top: 30, right: 20, bottom: 50, left: 60 };

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const xDomain = d3.extent(data, (d) => d.date) as [Date, Date];

    let yMin = d3.min(data, (d) => d.score)!;
    let yMax = d3.max(data, (d) => d.score)!;

    if (renderCfg.scale) {
      yMin = renderCfg.scale.min;
      yMax = renderCfg.scale.max;
    } else {
      yMin = Math.max(0, yMin - 1);
      yMax = yMax + 2;
    }

    const x = d3
      .scaleTime()
      .domain(xDomain)
      .range([margin.left, width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .range([height - margin.bottom, margin.top]);

    // Backgrounds & area
    let clipRect: d3.Selection<SVGRectElement, unknown, null, undefined> | null =
      null;

    if (data.length > 1) {
      svg
        .append("image")
        .attr("href", WeatherBgUrl)
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", chartWidth)
        .attr("height", chartHeight)
        .attr("preserveAspectRatio", "none")
        .attr("opacity", 0.2);

      const area = d3
        .area<{ date: Date; score: number }>()
        .x((d) => x(d.date))
        .y0(height - margin.bottom)
        .y1((d) => y(d.score))
        .curve(d3.curveMonotoneX);

      const areaPathId = `area-path-${uid}`;
      const revealClipId = `reveal-clip-${uid}`;
      const areaOnlyClipId = `area-only-clip-${uid}`;

      const areaPath = svg
        .append("path")
        .datum(data)
        .attr("d", area)
        .attr("id", areaPathId)
        .attr("fill", "none");

      const defs = svg.append("defs");

      clipRect = defs
        .append("clipPath")
        .attr("id", revealClipId)
        .append<SVGRectElement>("rect")
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", 0)
        .attr("height", chartHeight);

      const areaGroup = svg.append("g").attr("clip-path", `url(#${revealClipId})`);

      areaGroup
        .append("clipPath")
        .attr("id", areaOnlyClipId)
        .append("use")
        .attr("href", `#${areaPathId}`);

      areaGroup
        .append("image")
        .attr("href", WeatherBgUrl)
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", chartWidth)
        .attr("height", chartHeight)
        .attr("preserveAspectRatio", "none")
        .attr("clip-path", `url(#${areaOnlyClipId})`)
        .attr("opacity", 1);
    } else {
      svg
        .append("image")
        .attr("href", WeatherBgUrl)
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", chartWidth)
        .attr("height", chartHeight)
        .attr("preserveAspectRatio", "none")
        .attr("opacity", 1);

      const singlePoint = data[0];
      svg
        .append("circle")
        .attr("cx", x(singlePoint.date))
        .attr("cy", y(singlePoint.score))
        .attr("r", 20)
        .attr("fill", "#ff5722")
        .attr("opacity", 0.15);
    }

    // Axes
    const formatDate = d3.timeFormat("%a %d %b");
    const tickValues =
      data.length > 7
        ? data
            .filter((_, i) => i % Math.ceil(data.length / 7) === 0)
            .map((d) => d.date)
        : data.map((d) => d.date);

    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(tickValues)
          .tickFormat((d) => formatDate(d as Date))
      )
      .selectAll("text")
      .attr("text-anchor", "end")
      .attr("transform", "rotate(-30)")
      .attr("dx", "-0.6em")
      .attr("dy", "0.15em")
      .style("font-size", "11px");

    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(5))
      .style("font-size", "12px");

    // Line + points
    const line = d3
      .line<{ date: Date; score: number }>()
      .x((d) => x(d.date))
      .y((d) => y(d.score))
      .curve(d3.curveMonotoneX);

    const path = svg
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "#333")
      .attr("stroke-width", 2)
      .attr("d", line);

    // ---- points: circles | emojis | sprites ----
    let pointsSelection: d3.Selection<any, any, any, any>;

    const isEmojiMode =
      renderCfg.chartType === "emojis" && renderCfg.scale?.mode === "emoji";

    const isSpriteMode =
      renderCfg.chartType === "sprites" &&
      renderCfg.scale?.mode === "sprite" &&
      renderCfg.spriteUrls;

    if (isEmojiMode && renderCfg.scale) {
      const getEmoji = renderCfg.scale.map;
      pointsSelection = svg
        .selectAll(".emoji")
        .data(data)
        .enter()
        .append("text")
        .attr("class", "emoji")
        .attr("x", (d) => x(d.date))
        .attr("y", (d) => y(d.score))
        .attr("text-anchor", "middle")
        .attr("alignment-baseline", "middle")
        .attr("font-size", 31)
        .attr("opacity", 0)
        .text((d) => getEmoji(d.score));
    } else if (isSpriteMode && renderCfg.scale && renderCfg.spriteUrls) {
      const getKey = renderCfg.scale.map;
      const spriteSize = 40;

      pointsSelection = svg
        .selectAll(".sprite")
        .data(data)
        .enter()
        .append("image")
        .attr("class", "sprite")
        .attr("x", (d) => x(d.date) - spriteSize / 2)
        .attr("y", (d) => y(d.score) - spriteSize / 2)
        .attr("width", spriteSize)
        .attr("height", spriteSize)
        .attr("opacity", 0)
        .attr("href", (d) => {
          const key = getKey(d.score) as WeatherKey;
          return renderCfg.spriteUrls?.[key] ?? "";
        });
    } else {
      // default: circles
      pointsSelection = svg
        .selectAll(".dot")
        .data(data)
        .enter()
        .append("circle")
        .attr("class", "dot")
        .attr("cx", (d) => x(d.date))
        .attr("cy", (d) => y(d.score))
        .attr("r", 5)
        .attr("fill", "#ff5722")
        .attr("opacity", 0);
    }

    const totalLength = path.node()?.getTotalLength() || 0;
    path.attr("stroke-dasharray", totalLength).attr("stroke-dashoffset", totalLength);

    const duration = 3500;

    let lastTime = Date.now();
    let progress = 0;
    let isPaused = false;
    let pauseEnd = 0;
    const revealedPoints = new Set<number>();

    const timer = d3.timer(function tick() {
      const now = Date.now();

      if (isPaused) {
        if (now < pauseEnd) return;
        isPaused = false;
        lastTime = now;
      }

      const delta = now - lastTime;
      lastTime = now;
      progress += delta / duration;
      if (progress > 1) progress = 1;

      const currentLength = totalLength * (1 - progress);
      path.attr("stroke-dashoffset", currentLength);

      const p = path.node()?.getPointAtLength(totalLength * progress);
      if (p) {
        if (clipRect) {
          clipRect.attr("width", p.x - margin.left);
        }
        pointsSelection.each(function (d: { date: Date; score: number }) {
          const el = d3.select(this);
          const key = +d.date;
          if (x(d.date) <= p.x && !revealedPoints.has(key)) {
            revealedPoints.add(key);

            if (isEmojiMode) {
              el
                .transition()
                .duration(250)
                .attr("opacity", 1)
                .attr("font-size", 40)
                .transition()
                .duration(150)
                .attr("font-size", 31);
            } else if (isSpriteMode) {
              el.transition().duration(250).attr("opacity", 1).transition().duration(150);
            } else {
              el
                .transition()
                .duration(250)
                .attr("opacity", 1)
                .attr("r", 8)
                .transition()
                .duration(150)
                .attr("r", 5);
            }

            isPaused = true;
            pauseEnd = now + 400;
          }
        });
      }

      if (progress >= 1) return true;
    });

    return () => {
      timer.stop();
      svg.selectAll("*").interrupt();
      svg.selectAll("*").remove();
    };
  }, [data, renderCfg, animateKey, bgReady]);

  return (
    <div style={{ textAlign: "center", width: "100%", position: "relative" }}>
      {/* optional placeholder while the bg is decoding */}
      {!bgReady && (
        <div style={{ width: "100%", maxWidth: 600, height: 400, opacity: 0.08 }} />
      )}

      <svg
        ref={svgRef}
        className="evo-chart"
        viewBox="0 0 600 400"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "auto", maxWidth: "600px" }}
      />
    </div>
  );
};

export default React.memo(EvolutionChart);